// @purpose ERROR
// Browser error capture (S3 CLIENT_RUNTIME): window errors, unhandled rejections and htmx
// response/send errors → POST CLIENT_ERRORS_PATH (max 5/min), plus an inline retry on the failing
// section (S5: never the whole page). Copy matches content/errors/copy.json "generic".

export const CLIENT_ERRORS_PATH = "/api/client-errors";
const LIMIT_PER_MIN = 5;
const COPY = {
  title: "That bit wobbled.",
  body: "Something bent the wrong way on our side. Your answers are saved.",
  action: "Try again",
};

const sent = [];

function allow(now = Date.now()) {
  while (sent.length && now - sent[0] > 60_000) sent.shift();
  if (sent.length >= LIMIT_PER_MIN) return false;
  sent.push(now);
  return true;
}

function pathOnly(url) {
  try {
    return new URL(url, location.href).pathname;
  } catch {
    return "";
  }
}

export function report(payload) {
  if (!allow()) return;
  const body = JSON.stringify({ url: location.pathname, ...payload });
  try {
    fetch(CLIENT_ERRORS_PATH, {
      method: "POST",
      body,
      keepalive: true,
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
    }).catch(() => {}); // reporting must never throw into the page
  } catch {
    // fetch unavailable (very old browser): drop silently, the page keeps working
  }
}

function retryNotice(target, retry) {
  const old = target.querySelector(":scope > .calm-error[data-client]");
  if (old) old.remove();
  const box = document.createElement("div");
  box.className = "calm-error";
  box.setAttribute("role", "alert");
  box.dataset.client = "1";
  const title = document.createElement("p");
  title.className = "calm-error__title";
  title.textContent = COPY.title;
  const body = document.createElement("p");
  body.className = "calm-error__body";
  body.textContent = COPY.body;
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "calm-error__retry";
  btn.textContent = COPY.action;
  btn.addEventListener("click", () => {
    box.remove();
    retry();
  });
  box.append(title, body, btn);
  target.prepend(box);
}

// The server answers 401 + `X-Calm-Auth: stale` when a signed-in browser's ~60 s session token expired
// before a lazy partial loaded (apps/core/auth/middleware.py). Refresh the token and repeat once, quietly.
const retried = new WeakMap(); // elt -> last retry time; at most one retry per element per 30 s

function retryStale(d, verb, path) {
  const xhr = d.xhr;
  const elt = d.elt;
  if (!xhr || xhr.status !== 401 || xhr.getResponseHeader("X-Calm-Auth") !== "stale") return false;
  if (!elt || Date.now() - (retried.get(elt) || 0) < 30000) return false;
  if (!window.Calm?.auth?.refresh || !window.htmx) return false;
  retried.set(elt, Date.now());
  window.Calm.auth
    .refresh()
    .then((ok) => {
      if (!ok) return location.reload();
      if (verb === "GET") window.htmx.ajax("GET", path, { source: elt, target: d.target || elt });
      else if (elt.tagName === "FORM" && elt.requestSubmit) elt.requestSubmit();
      else window.htmx.trigger(elt, "click");
    })
    .catch(() => location.reload());
  return true;
}

function onHtmxFailure(evt, kind) {
  const d = evt.detail || {};
  const cfg = d.requestConfig || {};
  const verb = (cfg.verb || "get").toUpperCase();
  const path = cfg.path || "";
  if (kind === "htmx:responseError" && retryStale(d, verb, path)) return;
  report({ kind, status: d.xhr ? d.xhr.status : null, path: pathOnly(path), verb });
  let target = d.target || d.elt;
  if (!target || target === document.body || target === document.documentElement) {
    target = document.getElementById("main") || document.body;
  }
  const elt = d.elt;
  retryNotice(target, () => {
    if (verb === "GET" && window.htmx) {
      window.htmx.ajax("GET", path, { source: elt, target: d.target || target });
    } else if (elt && elt.tagName === "FORM" && elt.requestSubmit) {
      elt.requestSubmit();
    } else if (elt && window.htmx) {
      window.htmx.trigger(elt, "click");
    }
  });
}

window.addEventListener("error", (e) => {
  if (!(e instanceof ErrorEvent)) return; // resource load errors bubble here too; skip them
  report({
    kind: "error",
    message: String(e.message || "").slice(0, 300),
    source: pathOnly(e.filename || ""),
    line: e.lineno || null,
    col: e.colno || null,
  });
});

window.addEventListener("unhandledrejection", (e) => {
  const r = e.reason;
  const message = r && r.message ? r.message : String(r);
  report({ kind: "unhandledrejection", message: message.slice(0, 300) });
});

document.addEventListener("htmx:responseError", (e) => onHtmxFailure(e, "htmx:responseError"));
document.addEventListener("htmx:sendError", (e) => onHtmxFailure(e, "htmx:sendError"));
